import type { LucideIcon } from "lucide-react";
import { Award, HeartHandshake, Scale } from "lucide-react";

// Datos institucionales centralizados de Grupo Jurídico San Juan.
// Edite este archivo para actualizar contenidos sin tocar los componentes.

export const FirmName = "Grupo Jurídico San Juan";
export const ProductionUrl = "https://gjuridicosanjuan.com";
export const LogoUrl = "/logo.png"; // Logotipo oficial del despacho

export const Phones = [
  { Label: "+506 2448-4047", Href: "tel:+50624484047" },
  { Label: "+506 2448-3539", Href: "tel:+50624483539" },
];
export const WhatsAppDisplay = "+506 8603-5364";
export const WhatsAppNumber = "50686035364";
export const Email = "info@gjuridicosanjuan.com";
export const EmailHref = `mailto:${Email}`;

export const Address =
  "San Juan Sur, Poás, Alajuela, 400 metros norte del Templo Católico, Finca La Kattia, oficinas de Grupo Jurídico San Juan.";
export const MapsUrl = "https://maps.app.goo.gl/Wwx6iRXHJNeDVpJ28";
export const MapsEmbedUrl =
  "https://www.google.com/maps?q=10.1034695,-84.2411568&z=17&output=embed";

export const DefaultWhatsAppMessage =
  "Hola, me gustaría recibir información sobre sus servicios legales y coordinar una cita.";

export function buildWhatsAppUrl(pMessage: string = DefaultWhatsAppMessage): string {
  return `https://wa.me/${WhatsAppNumber}?text=${encodeURIComponent(pMessage)}`;
}

export function buildServiceWhatsAppUrl(pServiceTitle: string): string {
  return buildWhatsAppUrl(
    `Hola, me gustaría recibir información sobre el servicio de ${pServiceTitle.toLowerCase()}.`,
  );
}

export const BusinessHours = [
  { Days: "Lunes a viernes", Hours: "8:00 a. m. a 5:00 p. m." },
  { Days: "Sábados", Hours: "8:00 a. m. a 12:00 m." },
];

export const NavLinks = [
  { Id: "inicio", Label: "Inicio", Href: "#inicio" },
  { Id: "servicios", Label: "Servicios", Href: "#servicios" },
  { Id: "nosotros", Label: "Nosotros", Href: "#nosotros" },
  { Id: "profesionales", Label: "Profesionales", Href: "#profesionales" },
  { Id: "ubicacion", Label: "Ubicación", Href: "#ubicacion" },
  { Id: "contacto", Label: "Contacto", Href: "#contacto" },
];

export const HeroContent = {
  Overline: "Abogados y Notarios · Poás, Alajuela",
  Title: "Asesoría legal y notarial con experiencia y confianza",
  Subtitle:
    "En Grupo Jurídico San Juan brindamos asesoría jurídica y servicios notariales con atención profesional, confiable y personalizada.",
};

export const AboutContent = {
  Overline: "Nuestra firma",
  Title: "Comprometidos con su tranquilidad jurídica",
  Text: "Somos una firma de abogados y notarios que ofrece una amplia gama de servicios legales y notariales, brindando soluciones profesionales adaptadas a las necesidades de nuestros clientes.",
};

export interface PrincipleItem {
  Id: string;
  Title: string;
  Description: string;
  Icon: LucideIcon;
}

export const Principles: PrincipleItem[] = [
  {
    Id: "atencion-personalizada",
    Title: "Atención personalizada",
    Description:
      "Cada consulta recibe atención directa y cercana, adaptada a la situación de cada cliente.",
    Icon: HeartHandshake,
  },
  {
    Id: "experiencia-profesional",
    Title: "Experiencia profesional",
    Description:
      "Un equipo de abogados y notarios con amplia trayectoria en el ejercicio del derecho.",
    Icon: Award,
  },
  {
    Id: "asesoria-juridica-notarial",
    Title: "Asesoría jurídica y notarial",
    Description:
      "Soluciones legales y notariales integrales, con el respaldo de un mismo despacho.",
    Icon: Scale,
  },
];

export interface ServiceCategory {
  Id: string;
  Label: string;
}

export const ServiceCategories: ServiceCategory[] = [
  { Id: "notarial", Label: "Derecho Notarial y Registral" },
  { Id: "corporativo", Label: "Derecho Corporativo y Mercantil" },
  { Id: "familia", Label: "Derecho de Familia y Sucesiones" },
  { Id: "contratos", Label: "Contratos y Otros Trámites" },
];

export interface ServiceItem {
  Id: number;
  Title: string;
  Description: string;
  CategoryId: string;
  Image: string;
}

export const Services: ServiceItem[] = [
  {
    Id: 1,
    Title: "Traspasos y donaciones de vehículos y motocicletas",
    Description:
      "Gestión integral de traspasos y donaciones de vehículos y motocicletas conforme a la normativa aplicable.",
    CategoryId: "notarial",
    Image: "https://images.unsplash.com/photo-1761264889404-a194af20ae90?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODR8MHwxfHNlYXJjaHwzfHxjYXIlMjBrZXlzJTIwbHV4dXJ5JTIwdmVoaWNsZSUyMGNsYXNzaWMlMjBjYXIlMjBjbG9zZSUyMHVwfGVufDB8fHx8MTc5MjE3MjA0Nnww&ixlib=rb-4.1.0&q=85&w=200&h=200&fit=crop",
  },
  {
    Id: 2,
    Title: "Traspasos y donaciones de propiedades",
    Description: "Asesoría y tramitación de transferencias y donaciones de bienes inmuebles.",
    CategoryId: "notarial",
    Image: "https://images.unsplash.com/photo-1779231927091-57962cf79e70?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzB8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBob3VzZSUyMGFyY2hpdGVjdHVyZSUyMGtleXMlMjByZXNpZGVudGlhbCUyMGRldGFpbHxlbnwwfHx8fDE3OTIxNzIwNDZ8MA&ixlib=rb-4.1.0&q=85&w=200&h=200&fit=crop",
  },
  {
    Id: 3,
    Title: "Inscripción de marcas comerciales",
    Description: "Asesoría para el registro y la protección jurídica de marcas comerciales.",
    CategoryId: "corporativo",
    Image: "https://images.unsplash.com/photo-1646568779353-b9d2b903b3e1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTV8MHwxfHNlYXJjaHwxfHx3YXglMjBzZWFsJTIwc3RhbXAlMjBsdXh1cnklMjBwYWNrYWdpbmclMjBjbG9zZSUyMHVwfGVufDB8fHx8MTc5MjE3MjA0Nnww&ixlib=rb-4.1.0&q=85&w=200&h=200&fit=crop",
  },
  {
    Id: 4,
    Title: "Constitución y reformas de sociedades",
    Description:
      "Constitución de sociedades mercantiles y modificación de su nombre, capital, domicilio, administración y otros aspectos legales.",
    CategoryId: "corporativo",
    Image: "https://images.unsplash.com/photo-1443527394413-4b820fd08dde?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MTJ8MHwxfHNlYXJjaHwzfHxjb3Jwb3JhdGUlMjBidWlsZGluZyUyMHNreXNjcmFwZXIlMjBhcmNoaXRlY3R1cmUlMjB1cHdhcmQlMjB2aWV3fGVufDB8fHx8MTc5MjE3MjA0Nnww&ixlib=rb-4.1.0&q=85&w=200&h=200&fit=crop",
  },
  {
    Id: 5,
    Title: "Hipotecas y prendas",
    Description:
      "Constitución y cancelación de hipotecas y prendas sobre bienes muebles e inmuebles.",
    CategoryId: "notarial",
    Image: "https://images.unsplash.com/photo-1483600516620-7254872369ae?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NDh8MHwxfHNlYXJjaHwzfHxjbGFzc2ljYWwlMjBiYW5rJTIwY29sdW1ucyUyMGFyY2hpdGVjdHVyZSUyMG1hcmJsZSUyMGRldGFpbHxlbnwwfHx8fDE3OTIxNzIwNTF8MA&ixlib=rb-4.1.0&q=85&w=200&h=200&fit=crop",
  },
  {
    Id: 6,
    Title: "Marcas de ganado",
    Description:
      "Tramitación de la inscripción de marcas de ganado para su identificación y acreditación registral.",
    CategoryId: "notarial",
    Image: "https://images.unsplash.com/photo-1498191923457-88552caeccb3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzJ8MHwxfHNlYXJjaHwxfHxjYXR0bGUlMjByYW5jaCUyMGZpZWxkJTIwZmFybSUyMGxhbmRzY2FwZSUyMGRldGFpbHxlbnwwfHx8fDE3OTIxNzIwNTF8MA&ixlib=rb-4.1.0&q=85&w=200&h=200&fit=crop",
  },
  {
    Id: 7,
    Title: "Poderes notariales",
    Description: "Elaboración y formalización de poderes especiales, generales y generalísimos.",
    CategoryId: "notarial",
    Image: "https://images.unsplash.com/photo-1518674660708-0e2c0473e68e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTN8MHwxfHNlYXJjaHwzfHxmb3VudGFpbiUyMHBlbiUyMHNpZ25pbmclMjBsZWdhbCUyMGRvY3VtZW50JTIwZG9jdW1lbnQlMjBkZXRhaWx8ZW58MHx8fHwxNzkyMTcyMDUxfDA&ixlib=rb-4.1.0&q=85&w=200&h=200&fit=crop",
  },
  {
    Id: 8,
    Title: "Matrimonios",
    Description: "Asesoramiento y formalización legal de matrimonios.",
    CategoryId: "familia",
    Image: "https://images.unsplash.com/photo-1622398925373-3f91b1e275f5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDR8MHwxfHNlYXJjaHwyfHx3ZWRkaW5nJTIwcmluZ3MlMjBkZXRhaWwlMjBjbG9zZSUyMHVwJTIwZ29sZCUyMGJhbmRzfGVufDB8fHx8MTc5MjE3MjA1MXww&ixlib=rb-4.1.0&q=85&w=200&h=200&fit=crop",
  },
  {
    Id: 9,
    Title: "Divorcios por mutuo acuerdo",
    Description: "Asesoría y tramitación de divorcios por mutuo consentimiento.",
    CategoryId: "familia",
    Image: "https://images.unsplash.com/photo-1589330694653-ded6df03f754?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MjJ8MHwxfHNlYXJjaHwyfHxzZXBhcmF0ZWQlMjByaW5ncyUyMGNvbnRyYWN0JTIwcGFwZXIlMjBkb2N1bWVudCUyMGNsb3NlJTIwdXB8ZW58MHx8fHwxNzkyMTcyMDU3fDA&ixlib=rb-4.1.0&q=85&w=200&h=200&fit=crop",
  },
  {
    Id: 10,
    Title: "Declaraciones juradas",
    Description:
      "Elaboración y protocolización de declaraciones juradas para distintos fines legales.",
    CategoryId: "notarial",
    Image: "https://images.unsplash.com/photo-1646568780058-e2c150d2bc6d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTV8MHwxfHNlYXJjaHwzfHx3YXglMjBzZWFsJTIwc3RhbXAlMjBsdXh1cnklMjBwYWNrYWdpbmclMjBjbG9zZSUyMHVwfGVufDB8fHx8MTc5MjE3MjA0Nnww&ixlib=rb-4.1.0&q=85&w=200&h=200&fit=crop",
  },
  {
    Id: 11,
    Title: "Testamentos",
    Description: "Asesoría, elaboración y formalización de testamentos.",
    CategoryId: "notarial",
    Image: "https://images.unsplash.com/photo-1455390582262-044cdead277a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTN8MHwxfHNlYXJjaHwxfHxmb3VudGFpbiUyMHBlbiUyMHNpZ25pbmclMjBsZWdhbCUyMGRvY3VtZW50JTIwZG9jdW1lbnQlMjBkZXRhaWx8ZW58MHx8fHwxNzkyMTcyMDUxfDA&ixlib=rb-4.1.0&q=85&w=200&h=200&fit=crop",
  },
  {
    Id: 12,
    Title: "Procesos sucesorios",
    Description: "Asesoría y gestión de procesos sucesorios.",
    CategoryId: "familia",
    Image: "https://images.unsplash.com/photo-1682179259680-1ad7fe8f6b95?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjV8MHwxfHNlYXJjaHwxfHx2aW50YWdlJTIwcG9ja2V0JTIwd2F0Y2glMjBjbG9jayUyMGRlc2slMjBkb2N1bWVudCUyMGxlZ2FjeXxlbnwwfHx8fDE3OTIxNzIwNTd8MA&ixlib=rb-4.1.0&q=85&w=200&h=200&fit=crop",
  },
  {
    Id: 13,
    Title: "Reconocimiento de hijos",
    Description: "Asesoramiento y formalización de reconocimientos de paternidad o maternidad.",
    CategoryId: "familia",
    Image: "https://images.unsplash.com/photo-1651861787284-bf1bb85bcec8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzZ8MHwxfHNlYXJjaHw0fHxhZHVsdCUyMGhhbmQlMjBob2xkaW5nJTIwY2hpbGQlMjBoYW5kJTIwZGV0YWlsJTIwbWFjcm8lMjBjbG9zZSUyMHVwfGVufDB8fHx8MTc5MjE3MjA1N3ww&ixlib=rb-4.1.0&q=85&w=200&h=200&fit=crop",
  },
  {
    Id: 14,
    Title: "Contratos de arrendamiento",
    Description:
      "Asesoría y elaboración de contratos de arrendamiento de bienes muebles o inmuebles, según corresponda.",
    CategoryId: "contratos",
    Image: "https://images.unsplash.com/photo-1776107469475-e073014f7128?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MDV8MHwxfHNlYXJjaHwxfHxkb29yJTIwa2V5JTIwbG9jayUyMGhvdXNlJTIwZW50cnklMjBrZXklMjBoYW5kb3ZlcnxlbnwwfHx8fDE3OTIxNzIwNTd8MA&ixlib=rb-4.1.0&q=85&w=200&h=200&fit=crop",
  },
];

export interface ProfessionalItem {
  Id: number;
  Name: string;
  Role: string;
  Initials: string;
  Profile: string;
}

export const Professionals: ProfessionalItem[] = [
  {
    Id: 1,
    Name: "Licda. María del Milagro Ugalde Víquez",
    Role: "Abogada y Notaria",
    Initials: "MU",
    Profile:
      "Abogada y Notaria con 25 años de experiencia, con énfasis en Derecho Mercantil, fideicomisos y trámites legales, brindando asesoría profesional, confiable y personalizada.",
  },
  {
    Id: 2,
    Name: "Lic. Javier Brenes Saénz",
    Role: "Abogado y Notario",
    Initials: "JB",
    Profile:
      "Profesional con amplia experiencia en matrimonios, divorcios, adopciones, reconocimiento de hijos y trámites legales y notariales, brindando atención profesional y confiable.",
  },
  {
    Id: 3,
    Name: "Lic. Víctor Julio Herrera Bolaños",
    Role: "Abogado y Notario",
    Initials: "VH",
    Profile:
      "Profesional con amplia experiencia en trámites de propiedades, traspasos de vehículos y motocicletas, así como gestiones legales y notariales, brindando atención profesional y confiable.",
  },
];

export const CtaContent = {
  Title: "¿Necesita asesoría legal o notarial?",
  Text: "Contáctenos para consultar sobre nuestros servicios y coordinar una cita con nuestro equipo.",
  ButtonLabel: "Agendar cita por WhatsApp",
};

export const FooterDescription =
  "Firma de abogados y notarios en Poás, Alajuela. Asesoría jurídica y servicios notariales con atención profesional, confiable y personalizada.";

export const HeroImageUrl =
  "https://images.unsplash.com/photo-1694409496215-d116011d0d1b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzh8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjBvZmZpY2UlMjBpbnRlcmlvciUyMGFyY2hpdGVjdHVyZSUyMGRhcmslMjB3YXJtJTIwbGlnaHRpbmd8ZW58MHx8fHwxNzkyMTY4NjU5fDA&ixlib=rb-4.1.0&q=85";
export const CtaImageUrl =
  "https://images.unsplash.com/photo-1585119192382-71236d43b689?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MjJ8MHwxfHNlYXJjaHxfHxsZWdhbCUyMGRvY3VtZW50JTIwc2lnbmF0dXJlJTIwcGFwZXIlMjBmb3VudGFpbiUyMHBlbiUyMGRlc2slMjBtaW5pbWFsfGVufDB8fHx8MTc5MjE2ODY1Nnww&ixlib=rb-4.1.0&q=85";
